import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Stack, useTheme } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useMemo, useState } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { Divider } from "@/Components/v2/atoms/Divider";
import RoundedBorderContainer from "@/Components/v2/atoms/RoundedBorderContainer/RoundedBorderContainer";
import { Tooltip } from "@/Components/v2/atoms/ToolTip";
import { Typography } from "@/Components/v2/atoms/Typography";
import { ViewModeSelector } from "@/Components/v2/molecule/ViewModeSelector/ViewModeSelector";
import { ChannelTotalROIMap } from "@/pages/channel-roi/types";
import ConditionalScroll from "@/shared/components/ConditionalScroll";

import { adStockToolTipContent } from "../../../constants/content";
import { getChartOption } from "./chartOptions";
import LegendItems from "./LegendItems";

type AdStockGraphProps = {
  data: ChannelTotalROIMap;
  height?: string;
  maxHeight?: string;
};

// Define sort options
type SortOption = "alphabetical" | "duration_desc" | "duration_asc" | "peak_desc" | "peak_asc";

const AdStockBarGraph = ({ data, height = "75vh", maxHeight = "700px" }: AdStockGraphProps) => {
  const theme = useTheme();
  const [showPeak, setShowPeak] = useState(true);
  const [showLength, setShowLength] = useState(true);
  const [sortBy, setSortBy] = useState<SortOption>("alphabetical");

  const handleSortChange = (value: string) => {
    setSortBy(value as SortOption);
  };

  const sortOptions = [
    { value: "alphabetical", label: "Alphabetical (A-Z)" },
    { value: "duration_desc", label: "Length (Highest first)" },
    { value: "duration_asc", label: "Length (Lowest first)" },
    { value: "peak_desc", label: "Peak (Latest first)" },
    { value: "peak_asc", label: "Peak (Earliest first)" },
  ];

  // Sort data based on selected sort option
  const sortedData = useMemo(() => {
    const entries = Object.entries(data);

    switch (sortBy) {
      case "duration_desc":
        entries.sort(([, a], [, b]) => b.adstockDuration - a.adstockDuration);
        break;
      case "duration_asc":
        entries.sort(([, a], [, b]) => a.adstockDuration - b.adstockDuration);
        break;
      case "peak_desc":
        entries.sort(([, a], [, b]) => b.adstockPeak - a.adstockPeak);
        break;
      case "peak_asc":
        // Fix: Use correct comparison for ascending order
        entries.sort(([, a], [, b]) => a.adstockPeak - b.adstockPeak);
        break;
      case "alphabetical":
      default:
        entries.sort(([a], [b]) => a.localeCompare(b));
        break;
    }

    return Object.fromEntries(entries);
  }, [data, sortBy]);

  const option = useMemo(
    () => getChartOption(sortedData, theme, showLength, showPeak),
    [sortedData, theme, showLength, showPeak]
  );

  const adstockDataLength = Object.keys(sortedData).length;

  const PX_PER_COLUMN = 120;
  const SCROLL_THRESHOLD = 9;

  if (Object.keys(sortedData).length === 0) {
    return (
      <Stack px={4}>
        <RoundedBorderContainer>
          <Box mb={4}>
            <Typography variant="h4" fontWeight={600} fontSize={24}>
              Ad Stocks
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Stack alignItems="center" justifyContent="center" py={8}>
            <Typography variant="body1" fontWeight={600} textAlign="center">
              AdStocks are not available at this marketing level
            </Typography>
            <Typography variant="body2" textAlign="center" mt={1}>
              To view Ad Stocks for this business unit, reset funnel stage to All Marketing.
            </Typography>
          </Stack>
        </RoundedBorderContainer>
      </Stack>
    );
  }

  return (
    <Stack px={4}>
      <RoundedBorderContainer>
        <Box mb={4}>
          <Typography variant="h4" fontWeight={600} fontSize={24}>
            Ad Stocks
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Stack direction="row" justifyContent="space-between" sx={{ ml: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <Typography variant="body1" fontWeight={600}>
              Weeks
            </Typography>
            <Tooltip
              title={
                <Typography variant="body1" padding={1} sx={{ color: theme.palette.common.white }}>
                  {adStockToolTipContent}
                </Typography>
              }
              placement="bottom"
            >
              <HelpOutlineOutlinedIcon sx={{ color: theme.palette.brandedTypography.textColor }} />
            </Tooltip>
          </Stack>

          <ViewModeSelector
            value={sortBy}
            options={sortOptions}
            onChange={handleSortChange}
            width={250}
            label="Sort"
          />
        </Stack>
        <ConditionalScroll
          unitWidthPx={PX_PER_COLUMN}
          scrollThreshold={SCROLL_THRESHOLD}
          numberOfItems={adstockDataLength}
        >
          <ReactECharts
            option={option}
            style={{ height, maxHeight }}
            notMerge={true}
            lazyUpdate={true}
          />
        </ConditionalScroll>
        <Stack direction="row" spacing={2} my={2} justifyContent="center">
          <LegendItems
            theme={theme}
            showLength={showLength}
            showPeak={showPeak}
            setShowLength={setShowLength}
            setShowPeak={setShowPeak}
          />
        </Stack>
      </RoundedBorderContainer>
    </Stack>
  );
};

export default AdStockBarGraph;
